import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import Hls from "hls.js";

const VideoPlayer = forwardRef(({ src, onPlay, onPause }, ref) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);

    useEffect(() => {
        const el = videoRef.current;
        if (!el || !src) return;

        // Clean up any previous hls instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        const isHls = /\.m3u8($|\?)/i.test(src);

        if (isHls) {
            if (Hls.isSupported()) {
                // const authToken = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
                // const hls = new Hls({
                //     xhrSetup: (xhr) => {
                //         if (authToken && authToken.trim() !== 'null null') {
                //             xhr.setRequestHeader('Authorization', authToken);
                //         }
                //     }
                // });
                const hls = new Hls();
                hls.loadSource(src);
                hls.attachMedia(el);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    el.playbackRate = 3;
                    el.play().catch(() => {});
                });
                hls.on(Hls.Events.ERROR, (_event, data) => {
                    if (data?.fatal) {
                        console.error('[VideoPlayer] HLS fatal error:', data.type, data.details);
                    }
                });
                hlsRef.current = hls;
            } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
                // Safari native HLS
                el.src = src;
                el.addEventListener('loadedmetadata', () => {
                    el.playbackRate = 3;
                    el.play().catch(() => {});
                }, { once: true });
            } else {
                console.error('[VideoPlayer] HLS is not supported in this browser.');
            }
        } else {
            el.src = src;
            el.addEventListener('loadedmetadata', () => {
                el.playbackRate = 3;
                el.play().catch(() => {});
            }, { once: true });
        }

        const handlePlay = () => onPlay && onPlay();
        const handlePause = () => onPause && onPause();
        const handleEnded = () => onPause && onPause();
        const handleContextMenu = (e) => e.preventDefault();

        el.addEventListener('play', handlePlay);
        el.addEventListener('pause', handlePause);
        el.addEventListener('ended', handleEnded);
        el.addEventListener('contextmenu', handleContextMenu);
        el.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
        el.setAttribute('disablePictureInPicture', 'true');

        return () => {
            el.removeEventListener('play', handlePlay);
            el.removeEventListener('pause', handlePause);
            el.removeEventListener('ended', handleEnded);
            el.removeEventListener('contextmenu', handleContextMenu);
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            el.removeAttribute('src');
            el.load();
        };
    }, [src]);

    useImperativeHandle(ref, () => ({
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        isPaused: () => videoRef.current?.paused,
        setSpeed: (rate) => { if (videoRef.current) videoRef.current.playbackRate = rate; },
        getSpeed: () => videoRef.current?.playbackRate,
    }));

    return (
        <div className="video-container">
            <video
                ref={videoRef}
                controls
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '400px', display: 'block', margin: '0 auto', backgroundColor: 'transparent' }}
            />
            <style>{`
                .video-container {
                    max-width: 800px;
                    margin: 0 auto;
                }
                video::-webkit-media-controls-enclosure {
                    border-radius: 0;
                }
            `}</style>
        </div>
    );
});

export default VideoPlayer;
